<template>
  <form
    class="form-employee-work"
    v-on:submit.prevent="onSubmit"
  >
    <fieldset class="form-employee-work__options" :disabled="!hasEditSchedule">
      <legend class="form-employee-work__legend">Выберите режим работы</legend>
      <label class="form-employee-work__radio">
        <input
          name="work-mode"
          type="radio"
          v-bind:value="$options.workByClinic"
          v-model="workMode" />
        <span>Работает как организация</span>
      </label>
      <label class="form-employee-work__radio">
        <input
          name="work-mode"
          type="radio"
          v-bind:value="$options.workThroughDays"
          v-model="workMode" />
        <span>Дни через дни</span>
      </label>
      <days-through-days-picker
        v-show="workMode === $options.workThroughDays"
        v-bind:suggested-hours="suggestedHours"
        v-bind:initial-from="initialFrom"
        v-bind:initial-to="initialTo"
        v-model="daysThroughDays"
      />
      <label class="form-employee-work__radio">
        <input
          name="work-mode"
          type="radio"
          v-bind:value="$options.workEvenOddDays"
          v-model="workMode" />
        <span>Четные / нечетные дни</span>
      </label>
      <even-odd-days-picker
        v-show="workMode === $options.workEvenOddDays"
        v-bind:suggested-hours="suggestedHours"
        v-bind:initial-from="initialFrom"
        v-bind:initial-to="initialTo"
        v-model="evenOddDays"
      />
      <label class="form-employee-work__radio">
        <input
          name="work-mode"
          type="radio"
          v-bind:value="$options.workByWeekdays"
          v-model="workMode" />
        <span>По дням недели</span>
      </label>
      <weekdays-picker
        v-show="workMode === $options.workByWeekdays"
        v-bind:initial-date="weekdaysStartsDate"
        v-bind:initial-days="weekdays"
        v-bind:initial-from="initialFrom"
        v-bind:initial-to="initialTo"
        v-bind:suggested-hours="suggestedHours"
        v-on:change="onChangeWeekDay"
        v-on:input-date="onInputWeekdayStartsDate"
      />
      <label class="form-employee-work__radio">
        <input
          name="work-mode"
          type="radio"
          v-bind:value="$options.workFree"
          v-model="workMode" />
        <span>Свободный</span>
      </label>
      <label class="form-employee-work__radio">
        <input
          name="clear-work-period"
          type="button"
          v-on:click="onClearWorkSchedule"
        />
        <span>Очистить график</span>
      </label>
    </fieldset>
    <v-autocomplete
      v-if="!hideAssistants"
      clearable
      item-text="full_name"
      item-value="id"
      label="Ассистент"
      v-bind:items="assistants"
      v-bind:menu-props="menuSettings"
      v-model="assistantId"
    >
      <template v-slot:no-data>
        <p style="padding: 10px; margin:0;">Результатов нет</p>
      </template>
    </v-autocomplete>
    <div class="form-employee-work__buttons" v-if="hasEditSchedule">
      <button
        class="form-employee-work__cancel"
        type="button"
        v-on:click="closePopover"
      >Отмена</button>
      <button
        v-bind:disabled="isSubmitButtonDisabled"
        class="form-employee-work__submit"
        type="submit"
      >ОК</button>
    </div>
  </form>
</template>
<script>
import { builtInPopoverMixin } from '@/mixins/builtInPopoverMixin';
import DaysThroughDaysPicker, { DaysThroughDays } from '@/components/DaysThroughDaysPicker.vue';
import EvenOddDaysPicker, { EvenOddDays, EvenDays, OddDays } from '@/components/EvenOddDaysPicker.vue';
import WeekdaysPicker, { getWeekdays, getDefaultWeekdays } from '@/components/WeekdaysPicker.vue';

const makeDaysThroughDays = (from, to, options) => new DaysThroughDays(
  from,
  to,
  options.number_off_days,
  options.number_work_days,
  new Date(options.starts_date),
  options.work_days.map(
    (day) => day.map((workTimes) => ({ from: workTimes.starts_time, to: workTimes.ends_time })),
  ),
);

/**
 * @description Название события очистки графика работы.
 * @type {string}
 */
const NAME_EVENT_CLEAR_WORK_SCHEDULE = 'clear-work-schedule';

/**
 * @description Компонент формы выбора рабочего режима сотрудника.
 * @type {Vue}
 */
export default {
  components: {
    EvenOddDaysPicker,
    DaysThroughDaysPicker,
    WeekdaysPicker,
  },
  computed: {
    /**
     * @description Имя испускаемого события отправки.
     * @type {string}
     */
    emittedSubmitName: (vm) => ['submit', vm.workMode].join('-'),
    /**
     * @description Деактивировать кнопку отправки.
     * @type {boolean}
     */
    isSubmitButtonDisabled(vm) {
      return !vm.workMode || vm.workMode === 'empty';
    },
  },
  data() {
    const [
      evenOddDays,
      daysThroughDays,
      weekdays,
      weekdaysStartsDate,
    ] = this.prepareWorkModeOptions();
    return {
      /**
       * @description Четные нечетные дни.
       * @type {EvenOddDays}
       */
      evenOddDays,
      /**
       * @description Дни через дни.
       * @type {DaysThroughDays}
       */
      daysThroughDays,
      /**
       * @description Дни недели.
       * @type {WeekDay[]}
       */
      weekdays,
      /**
       * @description Дата начала отсчета для режима работы по дням недели.
       * @type {Date}
       */
      weekdaysStartsDate,
      /**
       * @description Режим работы.
       * @type {string}
       */
      workMode: this.initialWorkMode,
      /**
       * @description Идентификатор ассистента.
       */
      assistantId: this.initialAssistantId,
    };
  },
  methods: {
    prepareWorkModeOptions() {
      const evenOddDays = this.initialWorkMode === this.$options.workEvenOddDays
        ? new EvenOddDays(
          new EvenDays(
            this.initialFrom,
            this.initialTo,
            new Date(this.initialWorkModeOptions.even_days_starts_date),
            this.initialWorkModeOptions.even_days_work_times.map(
              (workTimes) => ({ from: workTimes.starts_time, to: workTimes.ends_time }),
            ),
          ),
          new OddDays(
            this.initialFrom,
            this.initialTo,
            new Date(this.initialWorkModeOptions.odd_days_starts_date),
            this.initialWorkModeOptions.odd_days_work_times.map(
              (workTimes) => ({ from: workTimes.starts_time, to: workTimes.ends_time }),
            ),
          ),
        ) : new EvenOddDays(
          new EvenDays(this.initialFrom, this.initialTo),
          new OddDays(this.initialFrom, this.initialTo),
        );
      const daysThroughDays = this.initialWorkMode === this.$options.workThroughDays
        ? makeDaysThroughDays(
          this.initialFrom,
          this.initialTo,
          this.initialWorkModeOptions,
        )
        : new DaysThroughDays(
          this.initialFrom,
          this.initialTo,
        );
      const isWorkByWeekdays = this.initialWorkMode === this.$options.workByWeekdays;
      const weekdays = isWorkByWeekdays
        ? getWeekdays(this.initialWorkModeOptions.weekdays, this.initialFrom, this.initialTo)
        : getDefaultWeekdays(this.initialFrom, this.initialTo);
      const weekdaysStartsDate = isWorkByWeekdays
        ? new Date(this.initialWorkModeOptions.starts_date)
        : new Date();
      return [evenOddDays, daysThroughDays, weekdays, weekdaysStartsDate];
    },
    /**
     * @description Обработать событие очистки графика работы.
     */
    onClearWorkSchedule() {
      this.$emit(NAME_EVENT_CLEAR_WORK_SCHEDULE);
    },
    onChangeWeekDay(dayIndex, day) {
      this.$set(this.weekdays, dayIndex, day);
    },
    onInputWeekdayStartsDate(value) {
      this.weekdaysStartsDate = value;
    },
    onSubmit() {
      const params = [];
      if (this.workMode === this.$options.workByWeekdays) {
        const weekdays = this
          .weekdays
          .filter((candidate) => candidate.isChecked)
          .map((candidate) => ({
            value: candidate.value,
            work_times: candidate
              .workTimes
              .map((workTimes) => ({ starts_time: workTimes.from, ends_time: workTimes.to })),
          }));
        const startsDate = this.weekdaysStartsDate.toISOString().split('T')[0];
        params.push(weekdays, startsDate);
      }
      if (this.workMode === this.$options.workThroughDays) {
        params.push(
          this.daysThroughDays.numberOffDays,
          this.daysThroughDays.startsDate.toISOString().split('T')[0],
          this.daysThroughDays.workDays.map(
            (day) => day.map((workTimes) => (
              { starts_time: workTimes.from, ends_time: workTimes.to }
            )),
          ),
        );
      }
      if (this.workMode === this.$options.workEvenOddDays) {
        params.push(
          this.evenOddDays.evenDays.startsDate.toISOString().split('T')[0],
          this.evenOddDays.evenDays.workTimes.map((workTimes) => (
            { starts_time: workTimes.from, ends_time: workTimes.to }
          )),
          this.evenOddDays.oddDays.startsDate.toISOString().split('T')[0],
          this.evenOddDays.oddDays.workTimes.map((workTimes) => (
            { starts_time: workTimes.from, ends_time: workTimes.to }
          )),
        );
      }
      this.$emit(this.emittedSubmitName, ...params, this.closePopover);
    },
  },
  mixins: [
    builtInPopoverMixin,
  ],
  name: 'FormEmployeeWorkMode',
  props: {
    /**
     * @description Может редактировать график работы.
     * @type {boolean}
     */
    hasEditSchedule: { required: true, type: Boolean },
    /**
     * @description Скрывать ассистентов.
     */
    hideAssistants: { default: false, type: Boolean },
    /**
     * @description Режим работы.
     * @type {string}
     */
    initialWorkMode: {
      default: 'free',
      type: String,
    },
    /**
     * @description Настройки режима работы.
     * @type {any}
     */
    initialWorkModeOptions: {
      type: [Object, Array],
      default: () => {},
    },
    /**
     * @description Настройки для раскрывающегося меню.
     * @type {object}
     */
    menuSettings: { default: () => ({}), type: Object },
    /**
     * @description Предложенные часы работы.
     */
    suggestedHours: { default: () => [], type: Array },
    /**
     * @description Часы работы от.
     */
    initialFrom: { default: () => '00:00', type: String },
    /**
     * @description Часы работы до.
     */
    initialTo: { default: () => '00:00', type: String },
    /**
     * @description Список ассистентов.
     */
    assistants: { default: () => [], type: Array },
    /**
     * @description Идентификатор ассистента.
     */
    initialAssistantId: { default: () => null, type: Number },
  },
  watch: {
    initialWorkMode(value) {
      this.workMode = value;
    },
    initialWorkModeOptions() {
      [
        this.evenOddDays,
        this.daysThroughDays,
        this.weekdays,
        this.weekdaysStartsDate,
      ] = this.prepareWorkModeOptions();
    },
    assistantId() {
      this.$emit('submit-assistant-id', this.assistantId);
    },
  },
  workByClinic: 'by-clinic',
  workByWeekdays: 'by-weekdays',
  workFree: 'free',
  workThroughDays: 'through-days',
  workEvenOddDays: 'even-odd-days',
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
$button-font-size: 14px;
$button-height: 37px;
$button-width: 100%;
.form-employee-work {
  padding: 22px 15px;
  max-width: 420px;
  width: 99%;
  &__legend {
    display: block;
    width: 100%;
    font-weight: 600;
  }
  &__options {
    border: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__radio>input {
    position: absolute;
    z-index: -1;
    opacity: 0;
  }
  &__radio>span {
    display: inline-flex;
    align-items: center;
    user-select: none;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    cursor: pointer;
  }
  &__radio>span::before {
    content: '';
    display: inline-block;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    flex-grow: 0;
    border: 1px solid #90A0B7;
    border-radius: 50%;
    margin-right: 10px;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 50% 50%;
  }
  &__radio>input:not(:disabled):not(:checked)+span:hover::before {
    border: 1px solid #0489E0;
  }
  &__radio>input:checked+span::before {
    border: 1px solid #0489E0;
    background-color: #0489E0;
    box-shadow: inset 0 0 0 6px #fff;
  }
  &__buttons {
    display: grid;
    grid-template-columns: 123px 123px;
    grid-gap: 6px;
    justify-content: flex-end;
    margin: 10px 0 0 0;
  }
  &__cancel {
    @include buttons.btn-transparent($button-width, $button-height, $button-font-size);
  }
  &__submit {
    @include buttons.btn-blue($button-width, $button-height, $button-font-size);
    &:disabled {
      background-color: var(--unfocus-color);
    }
  }

}

</style>
